/**
 * Procedural pixel-art village / interior placeholders.
 * Temporary illustrated art — not final production assets.
 */

import Phaser from 'phaser';

/** Draw a soft vertical sky→ground backdrop into a graphics object. */
export function paintVillageBackdrop(
  g: Phaser.GameObjects.Graphics,
  width: number,
  height: number,
): void {
  // Sky → ground bands tuned so the building cluster sits in the mid/lower
  // portrait frame (layout only — same palette / shapes as before).
  const bands = [
    { y: 0, h: height * 0.18, c: 0x1a2744 },
    { y: height * 0.18, h: height * 0.14, c: 0x243556 },
    { y: height * 0.32, h: height * 0.12, c: 0x3a4f3a },
    { y: height * 0.44, h: height * 0.56, c: 0x2a3a28 },
  ];
  for (const band of bands) {
    g.fillStyle(band.c, 1);
    g.fillRect(0, band.y, width, band.h);
  }

  // Distant hills — centred under the gate / mid row
  const cx = width * 0.5;
  g.fillStyle(0x1e2e1c, 1);
  g.fillEllipse(cx - width * 0.28, height * 0.42, 200, 64);
  g.fillEllipse(cx + width * 0.28, height * 0.44, 220, 70);

  // Path down the village centre — stops near the bottom building row
  // so it doesn't leave a huge empty wedge under Tavern/Shrine.
  const pathTop = height * 0.28;
  const pathBottom = height * 0.86;
  g.fillStyle(0x5a4a32, 1);
  g.fillTriangle(
    cx - width * 0.07,
    pathTop,
    cx + width * 0.07,
    pathTop,
    cx + width * 0.14,
    pathBottom,
  );
  g.fillTriangle(
    cx - width * 0.07,
    pathTop,
    cx - width * 0.14,
    pathBottom,
    cx + width * 0.14,
    pathBottom,
  );
  g.fillStyle(0x6e5a3c, 0.55);
  g.fillTriangle(
    cx - width * 0.035,
    pathTop + height * 0.02,
    cx + width * 0.035,
    pathTop + height * 0.02,
    cx + width * 0.04,
    pathBottom,
  );
}

export function paintBuildingSilhouette(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  body: number,
  accent: number,
  kind: string,
): void {
  const left = x - w / 2;
  const top = y - h / 2;

  // Shadow
  g.fillStyle(0x000000, 0.28);
  g.fillEllipse(x, top + h - 4, w * 0.9, 14);

  // Body
  g.fillStyle(body, 1);
  g.fillRect(left + 8, top + h * 0.35, w - 16, h * 0.55);

  // Roof
  g.fillStyle(accent, 1);
  if (kind === 'dungeon_gate') {
    // Gate arch
    g.fillRect(left + 4, top + 8, w - 8, h * 0.4);
    g.fillStyle(0x1a1f2e, 1);
    g.fillRect(left + w * 0.28, top + h * 0.28, w * 0.44, h * 0.55);
    g.fillStyle(0xc9a227, 1);
    g.fillRect(left + w * 0.42, top + h * 0.48, 6, 14);
  } else if (kind === 'shrine') {
    g.fillTriangle(x, top, left + 4, top + h * 0.42, left + w - 4, top + h * 0.42);
    g.fillStyle(0xe8d5a3, 0.85);
    g.fillCircle(x, top + h * 0.55, 8);
  } else if (kind === 'tavern') {
    g.fillTriangle(x, top + 4, left + 2, top + h * 0.4, left + w - 2, top + h * 0.4);
    g.fillStyle(0xf2e6c8, 0.7);
    g.fillRect(left + w * 0.22, top + h * 0.5, 12, 12);
    g.fillRect(left + w * 0.62, top + h * 0.5, 12, 12);
  } else if (kind === 'blacksmith') {
    g.fillRect(left + 2, top + h * 0.2, w - 4, h * 0.22);
    g.fillStyle(0xff6b35, 0.9);
    g.fillCircle(x + 18, top + h * 0.55, 7);
    g.fillStyle(0x2a1a10, 1);
    g.fillRect(left + w * 0.35, top + h * 0.55, w * 0.3, h * 0.28);
  } else {
    // Training grounds — open posts + banner
    g.fillStyle(accent, 1);
    g.fillRect(left + 14, top + 10, 8, h * 0.7);
    g.fillRect(left + w - 22, top + 10, 8, h * 0.7);
    g.fillStyle(0xc9a227, 1);
    g.fillRect(left + 14, top + 10, w - 28, 10);
    g.fillStyle(body, 1);
    g.fillEllipse(x, top + h * 0.72, w * 0.7, 18);
  }

  // Window glow accents
  if (kind !== 'training_grounds' && kind !== 'dungeon_gate') {
    g.fillStyle(0xffe08a, 0.55);
    g.fillRect(left + w * 0.2, top + h * 0.52, 10, 10);
  }
}

export function paintInteriorBackdrop(
  g: Phaser.GameObjects.Graphics,
  width: number,
  height: number,
  wall: number,
  floor: number,
): void {
  g.fillStyle(wall, 1);
  g.fillRect(0, 0, width, height * 0.62);
  g.fillStyle(floor, 1);
  g.fillRect(0, height * 0.62, width, height * 0.38);
  // Floor boards
  g.lineStyle(1, 0x000000, 0.18);
  for (let i = 0; i < 8; i += 1) {
    const y = height * 0.62 + i * 28;
    g.lineBetween(0, y, width, y);
  }
  // Soft vignette bars
  g.fillStyle(0x000000, 0.22);
  g.fillRect(0, 0, width, 48);
  g.fillRect(0, height - 56, width, 56);
}
